import { createContext } from 'react';
import type { VideoViewMode } from './videoViewReducer';

/** This viewer's view of the call, as `VideoViewProvider` holds it. */
export interface VideoViewContextValue {
  mode: VideoViewMode;
  /** `mode === 'fullscreen'`, for the many readers that only ask that */
  isFullScreen: boolean;
  /** Show the call full screen. Ignored when there is no call to show. */
  enterFullScreen: () => void;
  /** Back to the corner tiles over the world. */
  exitFullScreen: () => void;

  /**
   * The pinned tile's `tileKey` (`identity:source`), or `null`: tiled (#124).
   * Local to this viewer, never sent anywhere.
   */
  pinnedKey: string | null;
  /** Pin a tile by its `tileKey`; pinning another one swaps. */
  pin: (key: string) => void;
  /** Back to the tiled layout. A screen share unpinned here never pins itself again while it lasts. */
  unpin: () => void;
  /**
   * While something else is pinned, the most recent live screen share: the
   * top bar offers it as "<name> is sharing · Pin". `null` otherwise.
   */
  offeredShareKey: string | null;
  /**
   * Report the call's tiles (every `tileKey`, and the screen shares among
   * them). Clears a pin whose tile has gone and lets a new share pin itself.
   * The video overlay calls it; nothing else needs to.
   */
  syncTiles: (keys: readonly string[], shareKeys: readonly string[]) => void;
}

/**
 * `null` outside a `VideoViewProvider`. Most readers go through
 * `useVideoView`, which insists on the provider; the video overlay reads it
 * directly because it also renders on its own (tests, the error boundary),
 * where it simply has no full-screen mode.
 */
export const VideoViewContext = createContext<VideoViewContextValue | null>(null);
